import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const PeopleLogin = () => {
  const [isSignup, setIsSignup] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nickname: '',
    gender: '',
    vibe: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginPeople, signupPeople } = useAuth();
  const navigate = useNavigate();

  const vibes = [
    { value: 'low', label: 'Low / struggling', emoji: '😔' },
    { value: 'okay', label: 'Okay / managing', emoji: '😐' },
    { value: 'hopeful', label: 'Hopeful', emoji: '🙂' },
    { value: 'calm', label: 'Calm', emoji: '😌' },
    { value: 'strong', label: 'Strong / motivated', emoji: '💪' },
  ];

  const genders = [
    { value: 'woman', label: 'Woman' },
    { value: 'man', label: 'Man' },
    { value: 'non-binary', label: 'Non-binary' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignup) {
        // Signup validation
        if (!formData.nickname) {
          setError('Please choose a nickname');
          setLoading(false);
          return;
        }
        if (!formData.vibe) {
          setError('Please select how you\'re feeling');
          setLoading(false);
          return;
        }

        const result = await signupPeople(formData);
        if (result.success) {
          navigate('/');
        } else {
          setError(result.error);
        }
      } else {
        // Login
        const result = await loginPeople(formData.email, formData.password);
        if (result.success) {
          navigate('/');
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-testid="people-login-page">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
          <img src="/logo.png" alt="Ment-Ally Logo" className="h-16 w-16" />
        </div>
        <h2 className="text-center text-3xl font-bold text-gray-900 mb-2">
          {isSignup ? 'Create your account' : 'Welcome back'}
        </h2>
        <p className="text-center text-base text-gray-600">
          {isSignup 
            ? 'Find support that feels right for you' 
            : 'Sign in to access your saved services'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card shadow-xl p-8">
          {/* Privacy Notice for Signup */}
          {isSignup && (
            <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🔒</span>
                <div>
                  <h4 className="font-semibold text-teal-900 mb-1">Your privacy matters</h4>
                  <p className="text-sm text-teal-800 leading-relaxed">
                    We only ask for a nickname – not your real name. Your information is private and secure.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-800 px-4 py-3 rounded-lg" data-testid="people-login-error">
                {error}
              </div>
            )}

            {isSignup && (
              <>
                {/* Nickname */}
                <div>
                  <label htmlFor="nickname" className="block text-sm font-semibold text-gray-700 mb-2">
                    Choose a nickname *
                  </label>
                  <input
                    id="nickname"
                    name="nickname"
                    type="text"
                    required
                    value={formData.nickname}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    data-testid="nickname-input"
                    placeholder="e.g., Hope, Journey, Alex..."
                  />
                  <p className="text-xs text-gray-500 mt-1">This is how you'll appear to yourself – we won't share it publicly</p>
                </div>

                {/* Vibe Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    What vibe feels closest to you right now? *
                  </label>
                  <div className="space-y-2">
                    {vibes.map((vibe) => (
                      <label
                        key={vibe.value}
                        className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.vibe === vibe.value
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="vibe"
                          value={vibe.value}
                          checked={formData.vibe === vibe.value}
                          onChange={handleChange}
                          className="sr-only"
                          data-testid={`vibe-${vibe.value}`}
                        />
                        <span className="text-3xl mr-3">{vibe.emoji}</span>
                        <span className="font-medium text-gray-900">{vibe.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Gender (Optional) */}
                <div>
                  <label htmlFor="gender" className="block text-sm font-semibold text-gray-700 mb-2">
                    Gender (optional)
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    data-testid="gender-select"
                  >
                    <option value="">Select (optional)</option>
                    {genders.map((gender) => (
                      <option key={gender.value} value={gender.value}>
                        {gender.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                data-testid="people-email-input"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                data-testid="people-password-input"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-gradient py-4 text-lg font-semibold disabled:opacity-50 rounded-lg"
              data-testid="people-submit-button"
            >
              {loading ? 'Please wait...' : isSignup ? 'Create account' : 'Sign in'}
            </button>
          </form>

          {/* Social Login Placeholder */}
          {isSignup && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium">Or continue with</span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                <button
                  type="button"
                  className="btn-outline py-3 px-4 text-sm"
                  disabled
                >
                  Google
                </button>
                <button
                  type="button"
                  className="btn-outline py-3 px-4 text-sm"
                  disabled
                >
                  Apple
                </button>
                <button
                  type="button"
                  className="btn-outline py-3 px-4 text-sm"
                  disabled
                >
                  Yahoo
                </button>
              </div>
              <p className="text-xs text-center text-gray-500 mt-3">Social login coming soon</p>
            </div>
          )}

          {/* Toggle */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsSignup(!isSignup);
                setError('');
              }}
              className="text-teal-600 hover:text-teal-700 font-semibold"
              data-testid="people-toggle-button"
            >
              {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
            </button>
          </div>

          {/* Back */}
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-gray-600 hover:text-teal-600 font-medium">
              ← Choose different account type
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeopleLogin;
